/**
 * 서비스 요청 관리 서비스
 * - 관리자: 전체 조회/상태 변경
 * - 부동산: 내 고객 조회/상태별 건수
 */
import { db } from '../firebase/config';
import {
  collection, doc, getDoc, getDocs, updateDoc,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { SERVICE_CATEGORIES, AREA_SIZES } from '../config/ieumConfig';
import { formatUserName } from '../utils/userName';
import {
  buildAttributionLabel,
  buildFirstReferralAuditStatusLabel,
  buildFirstReferralCaptureSourceLabel,
  formatSourceUrlForAdmin,
  resolveFirstReferralAudit,
  resolveReferralAttribution,
} from '../utils/referralAttribution';

const REQ_COL = 'service_requests';
const CUST_COL = 'customers';
const USER_COL = 'users';
const PARTNER_COL = 'partners';

// services 배열 → "이사 외 2건" 형태 한글 라벨
const buildCategoryLabel = (req) => {
  const services = Array.isArray(req.services) ? req.services : null;
  if (services && services.length > 0) {
    const first = SERVICE_CATEGORIES[services[0]]?.label || services[0];
    return services.length === 1 ? first : `${first} 외 ${services.length - 1}건`;
  }
  const single = req.category || req.service_category || '';
  if (!single) return '';
  return SERVICE_CATEGORIES[single]?.label || single;
};

// ───── 관리자: 전체 서비스 요청 조회 (+ customer/realtor/partner 조인) ─────
const toDateStr = (ts) => {
  if (!ts) return '';
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') d = new Date(ts);
  else { try { d = new Date(ts); } catch { return ''; } }
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// 한 번의 관리자 목록 조회 안에서는 동일 중개사 UID를 한 번만 읽는다.
// 활성 추천인과 최초 감사 추천인이 같거나 여러 요청에서 반복돼도 중복 Firestore 호출을 만들지 않는다.
const createRealtorDisplayNameLoader = () => {
  const cache = new Map();
  return async (realtorId) => {
    const normalizedId = String(realtorId || '').trim();
    if (!normalizedId) return '-';
    if (!cache.has(normalizedId)) {
      cache.set(normalizedId, Promise.all([
        getDoc(doc(db, USER_COL, normalizedId)),
        getDoc(doc(db, 'realtors', normalizedId)),
      ]).then(([userSnap, realtorSnap]) => {
        const userData = userSnap.exists() ? userSnap.data() : {};
        const realtorData = realtorSnap.exists() ? realtorSnap.data() : {};
        const businessName = realtorData.business_name || realtorData.businessName || '';
        const realtorName = userData.name || businessName || '-';
        return businessName && realtorName !== businessName
          ? `${realtorName} (${businessName})`
          : realtorName;
      }).catch(() => '-'));
    }
    return cache.get(normalizedId);
  };
};

export const getAllServiceRequests = async (filters = {}) => {
  const snap = await getDocs(collection(db, REQ_COL));
  let requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // 최신순 정렬 (클라이언트)
  requests.sort((a, b) => {
    const ta = (a.createdAt?.seconds || a.created_at?.seconds) || 0;
    const tb = (b.createdAt?.seconds || b.created_at?.seconds) || 0;
    return tb - ta;
  });

  // 필터 적용
  if (filters.category && filters.category !== 'all') {
    requests = requests.filter((r) => r.category === filters.category);
  }
  if (filters.hqStatus && filters.hqStatus !== 'all') {
    requests = requests.filter((r) => (r.hq_status || r.hqStatus) === filters.hqStatus);
  }

  const loadRealtorDisplayName = createRealtorDisplayNameLoader();

  // 고객/부동산/업체 정보 조인
  const enriched = await Promise.all(
    requests.map(async (req) => {
      // 신형 ApplyFormChat: service_requests 문서에 name/phone/moving_address 직접 저장
      let customerName = req.name || '-';
      let phone = req.phone || '';
      let address = req.moving_address || req.movingAddress || req.address || '';
      let pyeongExact = req.area_pyeong_exact ?? null;
      let areaSizeKey = req.area_size || req.areaSize || '';
      let memo = req.memo || '';

      // 희망일 — customer_requested_date(일정변경 다이얼) 최우선, 그다음 카테고리별 필드
      let desiredDate = req.customer_requested_date || req.customerRequestedDate
        || req.moving_date || req.movingDate
        || req.cleaning_date || req.cleaningDate
        || req.interior_date || req.interiorDate
        || req.preferred_date || req.preferredDate
        || '';

      // 구형: customer_id 가 있으면 customers 컬렉션에서 보완 조회
      const customerId = req.customer_id || req.customerId;
      if (customerId) {
        try {
          const custSnap = await getDoc(doc(db, CUST_COL, customerId));
          if (custSnap.exists()) {
            const c = custSnap.data();
            customerName = c.name || customerName;
            phone = c.phone || phone;
            address = c.address || c.movingAddress || c.moving_address || address;
            if (pyeongExact == null) pyeongExact = c.area_pyeong_exact ?? null;
            if (!areaSizeKey) areaSizeKey = c.area_size || c.areaSize || '';
            memo = c.memo || memo;
            if (!desiredDate) {
              desiredDate = c.moving_date || c.movingDate
                || c.cleaning_date || c.cleaningDate
                || c.interior_date || c.interiorDate
                || c.preferred_date || c.preferredDate
                || '';
            }
          }
        } catch { /* skip */ }
      }

      // 평수 라벨화: pyeong 숫자 있으면 "N평", 없으면 area_size 키 → 라벨
      const areaSize = pyeongExact != null
        ? `${pyeongExact}평`
        : (AREA_SIZES[areaSizeKey]?.label || areaSizeKey || '');

      const sourceUrl = req.source_url || req.sourceUrl || '';
      const attribution = resolveReferralAttribution({
        explicitRealtorId: req.source_realtor_id || req.sourceRealtorId,
        sourceUrl,
        source: req.source,
        sourceType: req.source_type || req.sourceType,
      });
      const sourceRealtorId = attribution.sourceRealtorId;
      const realtorDisplayName = await loadRealtorDisplayName(sourceRealtorId);
      const firstReferralAudit = resolveFirstReferralAudit({
        sourceUrl,
        activeRealtorId: sourceRealtorId,
        requestCreatedAt: req.createdAt || req.created_at,
      });
      const firstReferralAuditDisplayName = firstReferralAudit.firstReferralId
        ? await loadRealtorDisplayName(firstReferralAudit.firstReferralId)
        : '';

      let partnerName = '';
      const assignedPartnerId = req.assigned_partner_id || req.assignedPartnerId;
      if (assignedPartnerId) {
        try {
          const [pSnap, pDocSnap] = await Promise.all([
            getDoc(doc(db, USER_COL, assignedPartnerId)),
            getDoc(doc(db, 'partners', assignedPartnerId)),
          ]);
          if (pSnap.exists()) {
            const u = pSnap.data();
            const p = pDocSnap.exists() ? pDocSnap.data() : {};
            partnerName = formatUserName({
              ...u,
              business_name: p.business_name || p.businessName || u.business_name || '',
            }, '');
          }
        } catch { /* skip */ }
      }

      // 신형(services[]) / 구형(category) 통합 — 대시보드 집계용 단일 category 파생
      const derivedCategory = req.category
        || req.service_category
        || (Array.isArray(req.services) && req.services[0])
        || '';

      return {
        ...req,
        category: derivedCategory,
        assigned_partner_id: assignedPartnerId || null,
        customerName,
        phone,
        address,
        areaSize,
        memo,
        desiredDate,
        categoryLabel: buildCategoryLabel(req),
        realtorName: buildAttributionLabel(attribution, realtorDisplayName),
        attributionKind: attribution.kind,
        attributionRecovered: attribution.recoveredFromSourceUrl,
        sourceUrlDisplay: formatSourceUrlForAdmin(sourceUrl),
        firstReferralAuditStatus: firstReferralAudit.kind,
        firstReferralAuditRealtorName: firstReferralAudit.firstReferralId
          ? (firstReferralAuditDisplayName === '-'
            ? '추천부동산 (계정 확인 필요)'
            : firstReferralAuditDisplayName)
          : '',
        firstReferralAuditCapturedAt: toDateStr(firstReferralAudit.capturedAt),
        firstReferralAuditStatusLabel: buildFirstReferralAuditStatusLabel(firstReferralAudit),
        firstReferralAuditCaptureSourceLabel: buildFirstReferralCaptureSourceLabel(
          firstReferralAudit.captureSource,
        ),
        partnerName,
        hqStatus: req.hq_status || req.hqStatus || 'unread',
        partnerStatus: req.partner_status || req.partnerStatus || 'unread',
        createdAt: toDateStr(req.createdAt || req.created_at),
      };
    })
  );

  return enriched;
};

// ───── 관리자: 상태 변경 ─────
export const updateHqStatus = async (requestId, newStatus, data = {}) => {
  const ref = doc(db, REQ_COL, requestId);
  await updateDoc(ref, {
    hq_status: newStatus,
    ...data,
    updated_at: serverTimestamp(),
  });
};

// ───── 부동산: 내 고객 서비스 요청 조회 ─────
export const getRequestsByRealtor = async (realtorId) => {
  const q = query(
    collection(db, REQ_COL),
    where('source_realtor_id', '==', realtorId)
  );
  const snap = await getDocs(q);
  let requests = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  requests.sort((a, b) => {
    const ta = (a.created_at?.seconds || a.createdAt?.seconds) || 0;
    const tb = (b.created_at?.seconds || b.createdAt?.seconds) || 0;
    return tb - ta;
  });

  // 서비스 완료 보강 — hq_status 만으론 완료를 알 수 없다.
  // partner_assignments.status === 'completed' 인 SR 은 본사 정산 단계(settlement_check)에
  // 머물러 있어도 서비스 자체는 완료된 것이므로, serviceCompleted 플래그로 표시해
  // 소비 화면(실적/통계/리드/초대 등)이 정산과 무관하게 "완료"로 집계하도록 한다.
  const completedSrIds = new Set();
  try {
    const asgSnap = await getDocs(query(collection(db, 'partner_assignments'), where('status', '==', 'completed')));
    asgSnap.docs.forEach((a) => {
      const ad = a.data();
      const sid = ad.service_request_id || ad.serviceRequestId;
      if (sid) completedSrIds.add(sid);
    });
  } catch { /* 조회 실패 시 hq_status 기준만 사용 */ }

  const enriched = await Promise.all(
    requests.map(async (req) => {
      let customerName = req.name || '-';
      let phone = req.phone || '';
      let address = req.moving_address || req.movingAddress || '';
      let desiredDate = req.customer_requested_date || req.customerRequestedDate
        || req.moving_date || req.movingDate
        || req.cleaning_date || req.cleaningDate
        || req.interior_date || req.interiorDate
        || req.preferred_date || req.preferredDate
        || '';
      const customerId = req.customer_id || req.customerId;
      if (customerId) {
        try {
          const custSnap = await getDoc(doc(db, CUST_COL, customerId));
          if (custSnap.exists()) {
            const c = custSnap.data();
            customerName = c.name || customerName;
            phone = c.phone || phone;
            address = c.moving_address || c.address || address;
            if (!desiredDate) {
              desiredDate = c.moving_date || c.movingDate
                || c.cleaning_date || c.cleaningDate
                || c.interior_date || c.interiorDate
                || c.preferred_date || c.preferredDate
                || '';
            }
          }
        } catch { /* skip */ }
      }

      return {
        ...req,
        customerName,
        phone,
        address,
        desiredDate,
        hqStatus: req.hq_status || req.hqStatus || 'unread',
        partnerStatus: req.partner_status || req.partnerStatus || 'unread',
        createdAt: toDateStr(req.created_at || req.createdAt),
      };
    })
  );

  return enriched;
};

// ───── 부동산: 상태별 건수 카운트 ─────
export const getRequestCountsByStatus = async (realtorId) => {
  const q = query(
    collection(db, REQ_COL),
    where('source_realtor_id', '==', realtorId)
  );
  const snap = await getDocs(q);

  // 서비스 완료 보강 — hq_status 만으론 완료를 알 수 없다.
  // 실제 서비스 완료는 partner_assignments.status === 'completed'(업체 완료처리/자동완료)이므로
  // 완료된 배정의 service_request_id 집합을 만들어 완료 버킷 판정에 사용한다.
  const completedSrIds = new Set();
  try {
    const asgSnap = await getDocs(query(collection(db, 'partner_assignments'), where('status', '==', 'completed')));
    asgSnap.docs.forEach((a) => {
      const ad = a.data();
      const sid = ad.service_request_id || ad.serviceRequestId;
      if (sid) completedSrIds.add(sid);
    });
  } catch { /* 조회 실패 시 hq_status 기준만 사용 */ }

  // granular hq_status
  const counts = {
    total: 0,
    read: 0, assigned: 0, settlement_check: 0, settlement_done: 0, hq_review_needed: 0,
    unmatched_cancelled: 0,
    // legacy 버킷 (기존 코드 호환용 — 별도 계산)
    unread: 0, consulting: 0, pending: 0, cancelled: 0, completed: 0,
  };

  snap.docs.forEach((d) => {
    const data = d.data();
    const status = data.hq_status || data.hqStatus || 'unread';
    counts.total++;
    // granular
    if (status === 'read') counts.read++;
    else if (status === 'assigned') counts.assigned++;
    else if (status === 'settlement_check') counts.settlement_check++;
    else if (status === 'settlement_done') counts.settlement_done++;
    else if (status === 'hq_review_needed') counts.hq_review_needed++;
    else if (status === 'unmatched_cancelled') counts.unmatched_cancelled++;
    // legacy 버킷 — 서비스 완료(파트너 completed) 또는 정산완료(settlement_done)면 완료로 우선 집계
    const isCompleted = completedSrIds.has(d.id) || status === 'settlement_done';
    if (isCompleted) counts.completed++;
    else if (status === 'unread' || status === 'read') counts.unread++;
    else if (status === 'assigned') counts.consulting++;
    else if (status === 'settlement_check') counts.pending++;
    else if (status === 'cancelled' || status === 'unmatched_cancelled') counts.cancelled++;
  });

  return counts;
};

// ───── 관리자: 전체 상태별 건수 카운트 ─────
export const getAllRequestCounts = async () => {
  const snap = await getDocs(collection(db, REQ_COL));

  const counts = {
    total: 0, unread: 0, read: 0, assigned: 0,
    settlement_check: 0, settlement_done: 0, cancelled: 0, unmatched_cancelled: 0, hq_review_needed: 0,
    today: 0,
  };

  const today = new Date().toISOString().slice(0, 10);

  snap.docs.forEach((d) => {
    const data = d.data();
    counts.total++;
    const status = data.hq_status || data.hqStatus || 'unread';
    if (counts[status] !== undefined) counts[status]++;

    const ts = data.created_at || data.createdAt;
    let created = '';
    if (ts?.toDate) created = ts.toDate().toISOString().slice(0, 10);
    else if (ts?.seconds) created = new Date(ts.seconds * 1000).toISOString().slice(0, 10);
    if (created === today) counts.today++;
  });

  return counts;
};

// ───── 관리자: 파트너 목록 (배정용) ─────
// service_categories 가 users 가 아니라 partners 컬렉션에 박혀있는 경우가 많아서 둘 다 본다.
// users 우선 → 비어있으면 partners 컬렉션에서 보강.
export const getPartnerList = async () => {
  const [usersSnap, partnersSnap] = await Promise.all([
    getDocs(query(collection(db, USER_COL), where('role', '==', 'partner'))),
    getDocs(collection(db, PARTNER_COL)),
  ]);

  const partnerCatMap = new Map();
  partnersSnap.docs.forEach((d) => {
    const x = d.data();
    const cats = Array.isArray(x.service_categories)
      ? x.service_categories
      : (Array.isArray(x.serviceCategories) ? x.serviceCategories : []);
    partnerCatMap.set(d.id, {
      cats,
      companyName: x.company_name || x.business_name || x.name || '',
      managerName: x.owner_name || x.manager_name || x.representative_name || '',
      phone: x.manager_phone || x.business_phone || x.phone || '',
    });
  });

  return usersSnap.docs
    .filter((d) => {
      const status = d.data().status;
      // 비활성/거절/대기 제외 (active 만)
      return !status || status === 'active';
    })
    .map((d) => {
      const data = d.data();
      const userCats = Array.isArray(data.service_categories)
        ? data.service_categories
        : (Array.isArray(data.serviceCategories) ? data.serviceCategories : []);
      const partnerInfo = partnerCatMap.get(d.id);
      const cats = userCats.length > 0
        ? userCats
        : (partnerInfo?.cats || []);
      const companyName = partnerInfo?.companyName || data.company_name || data.business_name || '';
      const managerName = data.manager_name || data.owner_name || partnerInfo?.managerName || '';
      const fallbackName = companyName || managerName || data.name || '-';
      return {
        id: d.id,
        name: fallbackName,
        companyName,
        service_categories: cats,
        managerName,
        phone: data.phone || partnerInfo?.phone || '',
      };
    });
};
